import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { invalidateAllFishGroupQueries } from '@/domain/fishGroups/_invalidateQueries';
import {
    useSiteStockingAssignmentController_assign,
    useSiteStockingAssignmentController_unassign,
} from '@/services/hooks';
import { SiteStockingAssignmentController_previewLineage } from '@/services/services';

import type {
    StockingAssignmentResult,
    StockingAssignmentTarget,
    UseStockingAssignmentResult,
} from './stockingAssignment.types';
import { isStockingCycleNameTaken } from './stockingCycleNaming';
import { useCreateStockingCycle } from './useCreateStockingCycle';

const isSameStockingName = (a: string, b: string) => isStockingCycleNameTaken(a, [b]);

/**
 * "Add to stocking" and "Remove from stocking" on the board: a group's whole lineage goes
 * into a stocking of the site (an existing one, or one created first), or back to none. Each
 * resolves once the board and the moved groups' cached reads have refetched.
 */
export const useStockingAssignment = (
    clientId: number,
    locationId: number
): UseStockingAssignmentResult => {
    const queryClient = useQueryClient();
    const createCycle = useCreateStockingCycle(clientId, locationId);
    const assignMutation = useSiteStockingAssignmentController_assign();
    const unassignMutation = useSiteStockingAssignmentController_unassign();
    // Busy until the refetch is done too, so a second click can't save twice.
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    // A stocking created for an assignment that then failed: a retry for that same stocking
    // reuses it rather than creating a second one under its name.
    const [createdCycle, setCreatedCycle] = useState<{ id: number; name: string } | null>(null);

    // The board once, and every cached read of the client's groups: the moved groups' events
    // gain a stocking change, and their lineage previews are stale.
    const refetch = () =>
        Promise.all([
            invalidateAllFishGroupQueries(queryClient, clientId),
            queryClient.invalidateQueries({
                queryKey: [SiteStockingAssignmentController_previewLineage.key, clientId],
                refetchType: 'all',
            }),
        ]);

    const exclusive = async (
        run: () => Promise<StockingAssignmentResult>
    ): Promise<StockingAssignmentResult> => {
        if (inFlight.current) throw new Error('A stocking assignment is already running');
        inFlight.current = true;
        setBusy(true);
        try {
            return await run();
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    const targetCycle = async (
        target: StockingAssignmentTarget
    ): Promise<{ id: number; createdName: string | null }> => {
        if (target.kind === 'existing') return { id: target.stockingCycleId, createdName: null };
        const name = target.stocking.name.trim();
        if (createdCycle && isSameStockingName(createdCycle.name, name)) {
            return { id: createdCycle.id, createdName: createdCycle.name };
        }
        const created = await createCycle.create({ ...target.stocking, name });
        if (created === null) throw new Error('The new stocking came back without an id');
        setCreatedCycle({ id: created, name });
        return { id: created, createdName: name };
    };

    const assign = (fishGroupId: number, target: StockingAssignmentTarget) =>
        exclusive(async () => {
            const { id: stockingCycleId, createdName } = await targetCycle(target);
            const response = await assignMutation.mutateAsync({
                clientId,
                locationId,
                stockingCycleId,
                requestBody: { fishGroupIds: [fishGroupId] },
            });
            await refetch();
            return {
                stockingCycleId,
                createdName,
                movedCount: response.data.assigned.length,
                concurrentStockingIds: (response.data.warnings ?? [])
                    .filter((warning) => warning.code === 'CONCURRENT_ACTIVE_CYCLE')
                    .map((warning) => warning.otherCycleId),
            };
        });

    const unassign = (fishGroupId: number, stockingCycleId: number) =>
        exclusive(async () => {
            const response = await unassignMutation.mutateAsync({
                clientId,
                locationId,
                stockingCycleId,
                requestBody: { fishGroupIds: [fishGroupId] },
            });
            await refetch();
            return {
                stockingCycleId,
                createdName: null,
                movedCount: response.data.unassigned.length,
                concurrentStockingIds: [],
            };
        });

    return {
        assign,
        unassign,
        createdCycle,
        isLoading:
            busy || createCycle.isLoading || assignMutation.isPending || unassignMutation.isPending,
        error: assignMutation.error ?? unassignMutation.error ?? createCycle.error,
        reset: () => {
            createCycle.reset();
            assignMutation.reset();
            unassignMutation.reset();
        },
    };
};
